import { PERMISSIONS, type PermissionCode } from '@/lib/permissions'

export { CollectionsPage } from './collection-worklist'
export { GroupMeetingsPage } from './group-meetings'
export { CollectionSheetPage } from './collection-sheet'

export {
  collectionWorklistSearchSchema,
  groupMeetingsSearchSchema,
  collectionSheetSearchSchema,
  DEFAULT_PAGE_SIZE,
  COLLECTION_SHEET_TABS,
  type CollectionWorklistSearch,
  type GroupMeetingsSearch,
  type CollectionSheetSearch,
  type CollectionSheetTab,
} from './search'

/**
 * The routes this area provides, for registration in `src/router.tsx`.
 *
 * Three screens, in the order an operator moves through them: find the group, find the meeting the
 * sheet in front of you came off, key the sheet.
 *
 * ## Registering them
 *
 * Each entry names the component exported above and the search schema that belongs to it — the
 * schemas are exported from this file for exactly that purpose, and the screens already validate
 * the raw search through the same ones, so wiring `validateSearch` moves where the validation
 * happens and changes nothing else:
 *
 * - `/collections`                          `CollectionsPage`      `collectionWorklistSearchSchema`
 * - `/collections/$groupId`                 `GroupMeetingsPage`    `groupMeetingsSearchSchema`
 * - `/collections/$groupId/$meetingId`      `CollectionSheetPage`  `collectionSheetSearchSchema`
 *
 * `/collections` keeps its existing name and export, so the import already in `router.tsx`
 * continues to resolve against this directory without being touched.
 *
 * ## About the permission on each entry
 *
 * It is `groups.view` throughout, and that is not a placeholder. Every *read* these three screens
 * make is against `/groups`, `/groups/{id}` and `/groups/{id}/meetings`, and `groups.view` is what
 * the server checks on each of them — so it is the honest answer to "can this person open this
 * screen at all". The permissions that decide whether money can be *posted* are three others, they
 * are checked independently by three different endpoints, and they are gated per column inside the
 * sheet rather than at the door: an officer who may take savings but not cash collateral gets the
 * sheet with one column live and the other explained. Gating the route on any one of them would
 * shut that officer out of a screen they are entitled to work on.
 *
 * The navigation entry for this area is gated on `groups.meeting.record`, which is a stricter
 * courtesy filter on the menu item and is left as it stands.
 */
export interface CollectionRouteDefinition {
  path: string
  componentName: string
  permission: PermissionCode
}

export const collectionsRoutes: CollectionRouteDefinition[] = [
  {
    path: '/collections',
    componentName: 'CollectionsPage',
    permission: PERMISSIONS.groups.view,
  },
  {
    path: '/collections/$groupId',
    componentName: 'GroupMeetingsPage',
    permission: PERMISSIONS.groups.view,
  },
  {
    path: '/collections/$groupId/$meetingId',
    componentName: 'CollectionSheetPage',
    permission: PERMISSIONS.groups.view,
  },
]
